import React, { useCallback, useEffect, useState, useMemo } from 'react'
import Paper from '@mui/material/Paper'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Box
} from '@mui/material'
import { useAuth } from '../../../context/AuthContext'
import dayjs from 'dayjs'
import MyTableCellHeader from '../../fields/table/MyTableCellHeader'
import { toast } from 'react-toastify'
import { getObjectsWithFilter } from '../../../api/apiClientCrud'
import Toolbar from '../../toolbar/Toolbar'
import { useOutletContext } from 'react-router-dom'
import { accountingColums } from './AccountingFields'
import MyTableCell from '../../fields/table/MyTableCell'
import { useTheme, lighten } from '@mui/material/styles'
import PDFReader from '../../pdfReader/PDFReader'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import AccountingPDF from './AccountingPDF'
import { pdf } from '@react-pdf/renderer'

const getTotalAmount = (lines, type) => {
  if (!Array.isArray(lines)) return 0
  return lines.reduce((acc, item) => acc + (item[type] || 0), 0)
}

const compareValues = (valueA, valueB, direction) => {
  if (valueA == null && valueB == null) return 0
  if (valueA == null) return direction === 'asc' ? 1 : -1
  if (valueB == null) return direction === 'asc' ? -1 : 1

  let numericA = valueA
  let numericB = valueB

  if (typeof valueA === 'string' && (valueA.match(/^\d{4}-\d{2}-\d{2}/) || valueA.match(/^\d{2}\/\d{2}\/\d{4}/))) {
    numericA = dayjs(valueA).valueOf()
    numericB = dayjs(valueB).valueOf()

    if (isNaN(numericA) || isNaN(numericB)) {
      numericA = valueA
      numericB = valueB
    }
  }

  if (typeof numericA === 'string' && typeof numericB === 'string') {
    const result = numericA.localeCompare(numericB, 'fr', { sensitivity: 'base' })
    return direction === 'asc' ? result : -result
  }

  if (numericA < numericB) {
    return direction === 'asc' ? -1 : 1
  }
  if (numericA > numericB) {
    return direction === 'asc' ? 1 : -1
  }
  return 0
}

const sortData = (data, configs) => {
  if (!configs) return data

  let sortedData = JSON.parse(JSON.stringify(data))

  if (configs.line && configs.line.key) {
    const key = configs.line.key
    const direction = configs.line.direction

    sortedData.forEach((yearGroup) => {
      yearGroup.periodYearMonth.forEach((monthGroup) => {
        monthGroup.lines.sort((lineA, lineB) => {
          const valA = lineA[key]
          const valB = lineB[key]
          return compareValues(valA, valB, direction)
        })
      })
    })
  }

  if (configs.month && configs.month.key === 'periodYearMonth') {
    const direction = configs.month.direction

    sortedData.forEach((yearGroup) => {
      yearGroup.periodYearMonth.sort((monthA, monthB) => {
        return compareValues(monthA.yearMonth, monthB.yearMonth, direction)
      })
    })
  }

  if (configs.year && configs.year.key === 'periodYear') {
    const direction = configs.year.direction

    sortedData.sort((groupA, groupB) => {
      return compareValues(groupA.periodYear, groupB.periodYear, direction)
    })
  }

  return sortedData
}

const useGroupedData = (incomes, outcomes, taxes) => {
  return useMemo(() => {
    const combinedData = [
      ...outcomes.map((item) => ({
        id: `outcome-${item.id}`,
        date: item.date,
        debit: item.amount,
        description: item.description,
        periodYear: item.periodYear,
        yearMonth: item.periodYearMonth,
        credit: undefined
      })),
      ...incomes.map((item) => ({
        id: `income-${item.id}`,
        date: item.date,
        credit: item.amount,
        description: item.description,
        periodYear: item.periodYear,
        yearMonth: item.periodYearMonth,
        debit: undefined
      })),
      ...taxes.map((item) => ({
        id: `tax-${item.id}`,
        date: item.payment_date,
        debit: item.tax_total,
        description: item.description,
        periodYear: item.periodYear,
        yearMonth: item.periodYearMonth,
        credit: undefined
      }))
    ]

    return combinedData.reduce((acc, item) => {
      const { periodYear, yearMonth, ...line } = item

      let yearGroup = acc.find((g) => g.periodYear === periodYear)
      if (!yearGroup) {
        yearGroup = { periodYear, periodYearMonth: [] }
        acc.push(yearGroup)
      }

      let monthGroup = yearGroup.periodYearMonth.find((m) => m.yearMonth === yearMonth)
      if (!monthGroup) {
        monthGroup = { yearMonth, lines: [] }
        yearGroup.periodYearMonth.push(monthGroup)
      }

      monthGroup.lines.push({ ...line, yearMonth })

      return acc
    }, [])
  }, [outcomes, incomes, taxes])
}

const useAccountingData = (options, queryFilters) => {
  const [incomes, setIncomes] = useState([])
  const [outcomes, setOutcomes] = useState([])
  const [taxes, setTaxes] = useState([])
  const [isFiltered, setIsFiltered] = useState(false)

  const fetchData = useCallback(
    async (objectName, setter) => {
      try {
        const filters = Object.keys(queryFilters).length ? queryFilters : options.objectFilter()
        const res = await getObjectsWithFilter(objectName, filters)
        setter(res.data[objectName] || [])
      } catch (err) {
        toast.error(`Erreur lors du chargement des ${objectName}.`, { autoClose: 2000 })
        setter([])
      }
    },
    [options, queryFilters]
  )

  useEffect(() => {
    if (queryFilters) {
      fetchData('incomes', setIncomes)
      fetchData('outcomes', setOutcomes)
      fetchData('tax', setTaxes)
    }

    setIsFiltered(false)
  }, [fetchData, queryFilters])

  return { incomes, outcomes, taxes, isFiltered, setIsFiltered }
}

function Accounting() {
  const { user } = useAuth()
  const userRight = useOutletContext()
  const theme = useTheme()

  const [activePage, setActivePage] = useState(0)
  const [rowperpage, rowperpagechange] = useState(5)
  const [filters, setFilters] = useState(null)
  const [queryFilters, setQueryFilters] = useState({})
  const [showPdf, setShowPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')

  const [sortConfigs, setSortConfigs] = useState({
    year: { key: 'periodYear', direction: 'asc', mode: 1 },
    month: null,
    line: null
  })

  const options = useMemo(
    () => ({
      isExpand: false,
      isFilter: true,
      columns: accountingColums,
      objectFilter: () => ({ company_id: user?.company_id }),
      defaultSorted: 'periodYear',
      columnCount: accountingColums.length
    }),
    [user?.company_id]
  )

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      isFilter: true,
      columns: options.columns,
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        }
      ]
    }),
    [userRight, options.columns]
  )

  const headerStyle = { height: '30px', padding: 10 }
  const { incomes, outcomes, taxes, setIsFiltered } = useAccountingData(options, queryFilters)

  useEffect(() => {
    let baseFilter = options.objectFilter()

    if (filters !== null && filters !== '{null}') {
      try {
        const parsedFilters = JSON.parse(filters)
        const combinedFilters = { ...baseFilter, ...parsedFilters }

        const structuredQuery = {
          $and: Object.keys(combinedFilters).map((key) => ({ [key]: combinedFilters[key] }))
        }
        setQueryFilters(structuredQuery)
        setIsFiltered(true)
      } catch (error) {
        toast.error('Format de filtre invalide.', { autoClose: 2000 })
        setQueryFilters(baseFilter)
      }
    } else {
      setQueryFilters(baseFilter)
      setIsFiltered(false)
    }
  }, [filters, options, setIsFiltered])

  let groupedDataSet = useGroupedData(incomes, outcomes, taxes)

  const handleSort = useCallback((column, sortingState) => {
    const keyToStore = column.name
    let direction = sortingState === 1 ? 'asc' : sortingState === 2 ? 'desc' : null
    let newConfig = { key: keyToStore, direction, mode: sortingState }

    setSortConfigs((prevConfigs) => {
      const newSortConfigs = { ...prevConfigs }

      if (keyToStore === 'periodYear') {
        newSortConfigs.year = sortingState === 0 ? { key: 'periodYear', direction: 'asc', mode: 1 } : newConfig
        newSortConfigs.month = null
        newSortConfigs.line = null
      } else if (keyToStore === 'periodYearMonth') {
        newSortConfigs.month = sortingState === 0 ? null : newConfig
        newSortConfigs.line = null
      } else {
        newSortConfigs.line = sortingState === 0 ? null : newConfig
      }

      return newSortConfigs
    })
    setActivePage(0)
  }, [])

  const handlechangepage = (event, newpage) => setActivePage(newpage)
  const handleRowsPerPage = (event) => {
    rowperpagechange(+event.target.value)
    setActivePage(0)
  }

  useEffect(() => {
    let url
    const generatePdfFromComponent = async () => {
      try {
        const component = <AccountingPDF columns={options.columns} dataSet={groupedDataSet} />
        const doc = pdf(component)
        const blob = await doc.toBlob()
        url = URL.createObjectURL(blob)
        setPdfUrl(url)
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [options.columns, groupedDataSet])

  const getFieldValue = useCallback(
    (name, data) => {
      const fields = name.split('.')
      let dataField = data
      for (const field of fields) {
        if (dataField === undefined || dataField === null) return undefined
        dataField = dataField[field]
      }

      const columnDef = options.columns.find((elem) => elem.name === name || elem.display === name)
      if (!columnDef) return dataField

      if (columnDef.mapping) {
        return columnDef.mapping(dataField)
      }

      const type = columnDef.type
      if (type === 'currency' && dataField !== undefined && dataField !== null) {
        return `${Number(dataField).toFixed(2)} €`
      } else if (type === 'date' && dataField) {
        return dayjs(dataField).format(columnDef.format || 'DD/MM/YYYY')
      }

      return dataField
    },
    [options.columns]
  )

  const renderAccountingRows = useMemo(() => {
    const themeStripedColor = lighten(theme.palette.primary.main, 0.85)
    const totalRowBgColor = lighten(theme.palette.primary.main, 0.6)

    const oddRowColor = themeStripedColor
    const evenRowColor = '#ffffff'
    const rightBorderStyle = '1px solid rgba(224, 224, 224, 1)'

    if (!groupedDataSet || groupedDataSet.length === 0)
      return (
        <TableRow>
          <TableCell colSpan={options.columnCount || 6}>Aucune donnée disponible.</TableCell>
        </TableRow>
      )

    const sortedDataSet = sortData(groupedDataSet, sortConfigs, getFieldValue)
    const paginatedData = sortedDataSet.slice(activePage * rowperpage, activePage * rowperpage + rowperpage)

    return paginatedData.map((yearGroup) => {
      const totalCreditYear = yearGroup.periodYearMonth.reduce(
        (acc, month) => acc + getTotalAmount(month.lines, 'credit'),
        0
      )
      const totalDebitYear = yearGroup.periodYearMonth.reduce(
        (acc, month) => acc + getTotalAmount(month.lines, 'debit'),
        0
      )

      const contentRowCount = yearGroup.periodYearMonth.reduce((sum, month) => sum + month.lines.length + 1, 0)
      const yearRowSpan = contentRowCount + 1

      let currentLineIndex = 0

      return (
        <React.Fragment key={`year-${yearGroup.periodYear}`}>
          {yearGroup.periodYearMonth.map((monthGroup, monthIndex) => {
            const totalCreditMonth = getTotalAmount(monthGroup.lines, 'credit')
            const totalDebitMonth = getTotalAmount(monthGroup.lines, 'debit')
            const monthRowSpan = monthGroup.lines.length + 1
            const firstLine = monthGroup.lines[0]
            const remainingLines = monthGroup.lines.slice(1)
            const minCellPadding = { paddingY: '4px', paddingX: '12px' }
            const firstLineBgColor = currentLineIndex % 2 === 0 ? evenRowColor : oddRowColor

            return (
              <React.Fragment key={`month-${monthGroup.yearMonth}`}>
                <TableRow key={firstLine ? firstLine.id : `month-header-${monthGroup.yearMonth}`}>
                  {monthIndex === 0 && (
                    <TableCell
                      rowSpan={yearRowSpan}
                      key={`year-cell-${yearGroup.periodYear}`}
                      sx={{
                        verticalAlign: 'top',
                        borderRight: rightBorderStyle,
                        borderBottom: 'none'
                      }}
                    >
                      <MyTableCell value={yearGroup.periodYear} />
                    </TableCell>
                  )}

                  <TableCell
                    rowSpan={monthRowSpan}
                    key={`month-cell-${monthGroup.yearMonth}`}
                    sx={{
                      ...minCellPadding,
                      borderRight: rightBorderStyle,
                      borderBottom: 'none'
                    }}
                  >
                    <MyTableCell value={monthGroup.yearMonth} />
                  </TableCell>

                  {firstLine &&
                    (() => {
                      currentLineIndex++

                      return options.columns.slice(2).map((column) => {
                        if (!column.onlyHeader) {
                          const value = getFieldValue(column.display || column.name, firstLine)
                          return (
                            <TableCell
                              key={`cell-${column.name}-${firstLine.id || 0}`}
                              sx={{ ...minCellPadding, backgroundColor: firstLineBgColor }}
                            >
                              <MyTableCell value={value} component={column.component} />
                            </TableCell>
                          )
                        }
                        return null
                      })
                    })()}
                </TableRow>

                {remainingLines.map((line, lineIndex) => {
                  const rowBgColor = currentLineIndex % 2 === 0 ? evenRowColor : oddRowColor
                  currentLineIndex++

                  return (
                    <TableRow key={line.id}>
                      {options.columns.slice(2).map((column) => {
                        if (!column.onlyHeader) {
                          const value = getFieldValue(column.display || column.name, line)
                          return (
                            <TableCell
                              key={`cell-${column.name}-${line.id || lineIndex}`}
                              sx={{ ...minCellPadding, backgroundColor: rowBgColor }}
                            >
                              <MyTableCell value={value} component={column.component} />
                            </TableCell>
                          )
                        }
                        return null
                      })}
                    </TableRow>
                  )
                })}

                <TableRow key={`month-total-${monthGroup.yearMonth}`} sx={{ fontWeight: 'bold' }}>
                  <TableCell colSpan={2} align='right'>
                    {`Total ${monthGroup.yearMonth}:`}
                  </TableCell>
                  <TableCell>{totalCreditMonth.toFixed(2)} €</TableCell>
                  <TableCell>{totalDebitMonth.toFixed(2)} €</TableCell>
                </TableRow>
              </React.Fragment>
            )
          })}

          <TableRow
            key={`year-total-${yearGroup.periodYear}`}
            sx={{
              fontWeight: 'bolder',
              backgroundColor: totalRowBgColor,
              '& > td': {
                paddingY: 0.5,
                minHeight: '20px'
              }
            }}
          >
            <TableCell colSpan={3} align='right' sx={{ borderBottom: 'none' }}>
              {`Total ${yearGroup.periodYear}:`}
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>{totalCreditYear.toFixed(2)} €</TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>{totalDebitYear.toFixed(2)} €</TableCell>
          </TableRow>
        </React.Fragment>
      )
    })
  }, [groupedDataSet, activePage, rowperpage, options.columns, getFieldValue, theme, sortConfigs])

  const grandTotalCredit = useMemo(() => {
    return groupedDataSet.reduce(
      (acc, yearGroup) =>
        acc + yearGroup.periodYearMonth.reduce((yAcc, month) => yAcc + getTotalAmount(month.lines, 'credit'), 0),
      0
    )
  }, [groupedDataSet])

  const grandTotalDebit = useMemo(() => {
    return groupedDataSet.reduce(
      (acc, yearGroup) =>
        acc + yearGroup.periodYearMonth.reduce((yAcc, month) => yAcc + getTotalAmount(month.lines, 'debit'), 0),
      0
    )
  }, [groupedDataSet])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', flexGrow: 1 }}>
      <Toolbar options={toolbar} setIsFiltered={setIsFiltered} setFilters={setFilters} />

      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`comptabilité.pdf`} />
        </Box>
      ) : (
        <>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', pb: 1 }}>
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                height: '100%',
                width: '90%',
                marginLeft: '5%'
              }}
            >
              <TableContainer sx={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow style={headerStyle}>
                      {options.columns.map((column) => {
                        const isColumnSortable = column.isSortable !== false
                        const comparisonKey = column.name

                        let activeSortConfig = null
                        if (comparisonKey === 'periodYear') {
                          activeSortConfig = sortConfigs.year
                        } else if (comparisonKey === 'periodYearMonth') {
                          activeSortConfig = sortConfigs.month
                        } else {
                          activeSortConfig = sortConfigs.line
                        }

                        const isCurrentlySorted = activeSortConfig && activeSortConfig.key === comparisonKey
                        const sortedColumnMode = isCurrentlySorted ? activeSortConfig.mode : 0

                        return (
                          <MyTableCellHeader
                            header={column}
                            key={`header-${column.name}`}
                            sort={isColumnSortable ? handleSort : undefined}
                            sortedColumn={comparisonKey}
                            sortedColumnMode={sortedColumnMode}
                          />
                        )
                      })}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {renderAccountingRows}

                    <TableRow sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}>
                      <TableCell colSpan={options.columnCount - 2} align='right' sx={{ borderBottom: 'none' }}>
                        {`Total :`}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>{grandTotalCredit.toFixed(2)} €</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>{grandTotalDebit.toFixed(2)} €</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}>
                      <TableCell colSpan={options.columnCount - 1} align='right' sx={{ borderBottom: 'none' }}>
                        {`Bénéfice :`}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {(grandTotalCredit - grandTotalDebit).toFixed(2)} €
                      </TableCell>
                    </TableRow>
                  </TableBody>

                  <TableFooter sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                    <TableRow>
                      <TableCell colSpan={options.columnCount} sx={{ borderBottom: 'none' }}>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          rowsPerPage={rowperpage}
                          page={activePage}
                          count={groupedDataSet.length}
                          component='div'
                          labelRowsPerPage={'Lignes par page :'}
                          onPageChange={handlechangepage}
                          onRowsPerPageChange={handleRowsPerPage}
                          labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                          }
                        />
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  )
}

export default Accounting
